import React, { createContext, useContext, useEffect, useState } from 'react';

export type DaaluuColor = 'white' | 'black';
const DAALUU_COLOR_KEY = 'big2.daaluuColor.v1';
function initialDaaluuColor(): DaaluuColor {
  try { return localStorage.getItem(DAALUU_COLOR_KEY) === 'black' ? 'black' : 'white'; }
  catch { return 'white'; }
}

export type CardBackId =
  | 'soyombo'
  | 'ulzii_emerald'
  | 'imperial_crimson'
  | 'cyber_neon'
  | 'obsidian_vip'
  | 'classic_blue';

export interface CardBackOption {
  id: CardBackId;
  nameMn: string;
  nameEn: string;
  descMn: string;
  descEn: string;
  badgeMn?: string;
  badgeEn?: string;
  paidOnly?: boolean;
}

export const CARD_BACKS: CardBackOption[] = [
  {
    id: 'classic_blue',
    nameMn: 'Сонгодог Хөх',
    nameEn: 'Classic Royal Blue',
    descMn: 'Сонгодог хөзрийн очир алмаазан хээтэй уламжлалт цэнхэр загвар.',
    descEn: 'Timeless classic diamond-lattice back in royal blue.',
    badgeMn: 'ҮНЭГҮЙ',
    badgeEn: 'FREE',
    paidOnly: false,
  },
  {
    id: 'soyombo',
    nameMn: 'Алтан Соёмбо',
    nameEn: 'Golden Soyombo',
    descMn: 'Мөнх тэнгэрийн хөх дэвсгэр дээрх алтан шаргал уламжлалт Соёмбо билэгдэл.',
    descEn: 'Traditional Mongolian golden Soyombo emblem on eternal-sky royal blue.',
    badgeMn: '👑 VIP · 🇲🇳',
    badgeEn: '👑 VIP · 🇲🇳',
    paidOnly: true,
  },
  {
    id: 'ulzii_emerald',
    nameMn: 'Номин Өлзий Хээ',
    nameEn: 'Emerald Ulzii Knot',
    descMn: 'Номин ногоон дэвсгэр дээрх урт удаан, өлзий бэлгэдлийн алтан хээ.',
    descEn: 'Deep emerald jade adorned with a golden Mongolian endless-knot motif.',
    badgeMn: '👑 VIP',
    badgeEn: '👑 VIP',
    paidOnly: true,
  },
  {
    id: 'imperial_crimson',
    nameMn: 'Хааны Улаан Алт',
    nameEn: 'Imperial Crimson',
    descMn: 'Торгомсог хүрэн улаан болон алтан титэм хосолсон тансаг загвар.',
    descEn: 'Velvet crimson red paired with golden filigree and royal crest.',
    badgeMn: '👑 VIP',
    badgeEn: '👑 VIP',
    paidOnly: true,
  },
  {
    id: 'cyber_neon',
    nameMn: 'Неон Киберпанк',
    nameEn: 'Neon Cyberpunk',
    descMn: 'Харанхуй шөнийн неон ягаан болон цэнхэр гэрэлт хэлхээ.',
    descEn: 'Dark futuristic grid with glowing cyan and magenta neon accents.',
    badgeMn: '👑 VIP',
    badgeEn: '👑 VIP',
    paidOnly: true,
  },
  {
    id: 'obsidian_vip',
    nameMn: 'Хар Алт VIP',
    nameEn: 'Obsidian Gold VIP',
    descMn: 'Гүн хар матт гадаргуу дээрх алтан геометрийн тансаг хүрээ.',
    descEn: 'Matte obsidian black with brushed metallic gold luxury geometry.',
    badgeMn: '👑 VIP',
    badgeEn: '👑 VIP',
    paidOnly: true,
  },
];

const STORAGE_KEY = 'big2.cardBack';

function getInitialCardBack(): CardBackId {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY) as CardBackId | null;
    if (saved && CARD_BACKS.some((b) => b.id === saved)) {
      return saved;
    }
  }
  return 'classic_blue';
}

interface CardBackContextValue {
  daaluuColor: DaaluuColor;
  setDaaluuColor: (color: DaaluuColor) => void;
  cardBack: CardBackId;
  setCardBack: (id: CardBackId) => void;
}

const CardBackContext = createContext<CardBackContextValue | null>(null);

export function CardBackProvider({ children }: { children: React.ReactNode }) {
  const [daaluuColor, setDaaluuColorState] = useState<DaaluuColor>(initialDaaluuColor);
  useEffect(() => {
    // The document scope also reaches collection animations portaled to body.
    document.documentElement.dataset.daaluuColor = daaluuColor;
    return () => { delete document.documentElement.dataset.daaluuColor; };
  }, [daaluuColor]);
  const setDaaluuColor = (color: DaaluuColor) => {
    if (color !== 'white' && color !== 'black') return;
    setDaaluuColorState(color);
    try { localStorage.setItem(DAALUU_COLOR_KEY, color); } catch { /* Preference remains usable without storage. */ }
  };
  const [cardBack, setCardBackState] = useState<CardBackId>(getInitialCardBack);

  const setCardBack = (id: CardBackId) => {
    setCardBackState(id);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, id);
    }
  };

  return (
    <CardBackContext.Provider value={{ cardBack, setCardBack, daaluuColor, setDaaluuColor }}>
      {children}
    </CardBackContext.Provider>
  );
}

export function useCardBack() {
  const ctx = useContext(CardBackContext);
  if (!ctx) throw new Error('useCardBack must be used within CardBackProvider');
  return ctx;
}
